import { useMemo } from 'react';
import ListTile from 'components/ListTile';
import UserAvatar from 'components/UserAvatar';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import getInitials from 'data/services/string/getInitials';
import useCustomerPanel from 'hooks/useCustomerPanel';
import { InvoiceRow, InvoiceSignatureInfo } from '../../types';
import { formatIsoDate } from '../../helpers';
import SignatureStateIcon from './signature';
import {
    CellWrapper,
    InvoiceName,
    InvoiceNumber,
    UserCellWrapper,
    VoidText,
} from '../../styled';

interface InvoiceColumnRow extends InvoiceRow {
    signature?: InvoiceSignatureInfo;
}

interface InvoiceColumnProps {
    row?: InvoiceColumnRow;
    onClick?: (invoice: InvoiceRow) => void;
}

interface InvoiceCellProps {
    row?: InvoiceRow;
}

const voidableText = (value: string, isVoid?: boolean) => (
    isVoid ? <VoidText>{value}</VoidText> : value
);

const InvoiceColumn = ({ row, onClick }: InvoiceColumnProps) => {
    if (!row) return null;
    return (
        <UserCellWrapper>
            <InvoiceName>
                <InvoiceNumber
                    type="button"
                    $isVoid={row.status === 'void'}
                    onClick={() => onClick?.(row)}
                >
                    {row.number}
                </InvoiceNumber>
                {row.signature ? (
                    <SignatureStateIcon signature={row.signature} />
                ) : null}
            </InvoiceName>
        </UserCellWrapper>
    );
};

export default InvoiceColumn;

export const CustomerColumn = ({ row }: InvoiceCellProps) => {
    const { openCustomerPanel } = useCustomerPanel();
    const customer = row?.customer;
    const displayName = customer?.name || 'Unknown';
    const initials = useMemo(() => {
        if (!customer?.name || displayName === 'Unknown') return null;
        return getInitials(displayName);
    }, [customer?.name, displayName]);

    if (!row) return null;
    if (!customer) return <CellWrapper>—</CellWrapper>;

    return (
        <UserCellWrapper>
            <ListTile
                icon={(
                    <UserAvatar
                        initials={initials}
                        value={customer.id}
                    />
                )}
                onClick={() => openCustomerPanel({ data: { id: customer.id } })}
                title={displayName}
                subtitle={formatIntlPhoneNumber(customer.phone) || customer.email}
            />
        </UserCellWrapper>
    );
};

export const IssuedColumn = ({ row }: InvoiceCellProps) => {
    if (!row) return null;
    return (
        <CellWrapper>
            {voidableText(formatIsoDate(row.issuedOn), row.status === 'void')}
        </CellWrapper>
    );
};

export const DueColumn = ({ row }: InvoiceCellProps) => {
    if (!row) return null;
    return (
        <CellWrapper>
            {voidableText(formatIsoDate(row.dueOn), row.status === 'void')}
        </CellWrapper>
    );
};

export const AmountColumn = ({ row }: InvoiceCellProps) => {
    if (!row) return null;
    const amount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: row.total.currency || 'USD',
    }).format(row.total.amountMinor / 100);
    return (
        <CellWrapper>
            {voidableText(amount, row.status === 'void')}
        </CellWrapper>
    );
};
